import { Form, Input, Select, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { ReactComponent as CheckSelectIcon } from '../../../../../common/images/svg/CheckSelect.svg'
import { ReactComponent as ArrowIcon } from '../../../../../common/images/svg/SuffixSelectIcon.svg'
import { CustomButton } from '../../../../../components/button/buttonCustom'
import { ButtonOutlinedCustom } from '../../../../../components/button/buttonOulined'
import {
  addressWalletRules,
  clearFieldErrorMessageOnChange,
} from '../../../../../utils/form.constants'
import { useMetadataCurrencyToken } from '../../../../../utils/hooks/useMetadataCurrencyToken'
import { useMetadataNetwork } from '../../../../../utils/hooks/useMetadataNetwork'
import useResponsive from '../../../../../utils/hooks/useResponsive'
import { createTronWebInstance } from '../../../../../utils/tronWeb'
import {
  getNetWorkByName,
  getSymbolByName,
} from '../../../../../utils/list.utils'
import { WalletBeneficiary } from '../../../type'
import { ethers } from 'ethers'

interface IFormWalletProps {
  onSubmitWallet: (values: WalletBeneficiary) => void
  onBack: () => void
  onSkip: () => void
  stepNewBeneficiary: number | null
  valueFormWallet: WalletBeneficiary | undefined
}

const FormWallet = ({
  onSubmitWallet,
  onBack,
  onSkip,
  stepNewBeneficiary,
  valueFormWallet,
}: IFormWalletProps) => {
  const [form] = Form.useForm<WalletBeneficiary>()
  const { isTablet } = useResponsive()
  const [valueSkipForm, setValueSkipForm] = useState<any>()
  const currency = Form.useWatch('currency', form)
  const network = Form.useWatch('network', form)
  const address = Form.useWatch('address', form)

  const [searchCurrencyToken, setSearchCurrencyToken] = useState<string>('')
  const [searchNetwork, setSearchNetwork] = useState<string>('')
  const [tronWeb, setTronWeb] = useState<any>(null)
  const [addressBlur, setAddressBlur] = useState(false)
  const [addressValue, setAddressValue] = useState<any>()

  const { metadataCurrencyToken, dataCurrencyToken } = useMetadataCurrencyToken(
    Number(network || 0)
  )

  const { metadataNetwork, dataNetwork } = useMetadataNetwork(
    Number(currency || 0)
  )

  const handleChangeCurrencyToken = (value: string) => {
    setSearchCurrencyToken(value)
  }

  const handleChangeNetwork = (value: string) => {
    setSearchNetwork(value)
  }

  useEffect(() => {
    if (
      !Array.isArray(metadataCurrencyToken) ||
      !Array.isArray(metadataNetwork)
    )
      return
    const currencyLabel = getSymbolByName(dataCurrencyToken, currency)
    const networkLabel = getNetWorkByName(dataNetwork, network)

    if (currencyLabel && networkLabel && address && address.length >= 4) {
      const last4 = address.slice(-4)
      form.setFieldsValue({ name: `${currencyLabel}-${networkLabel}-${last4}` })
    } else {
      form.setFieldsValue({ name: '' })
    }
  }, [
    currency,
    network,
    address,
    form,
    metadataCurrencyToken,
    metadataNetwork,
    valueSkipForm,
  ])

  const requiredFields = ['currency', 'network', 'address', 'name']
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(true)

  const handleTronWeb = async () => {
    try {
      // Wait for TronWebProto to be available (loaded by TronWeb.js script)
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)
      while (
        typeof globalThis !== 'undefined' &&
        !(globalThis as any).TronWebProto &&
        attempts < maxAttempts
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof globalThis === 'undefined' || !(globalThis as any).TronWebProto) {
        console.warn("TronWebProto not available, TronWeb functionality may be limited");
      }

      const { TronWeb } = await import('tronweb')
      const tronWebInstance = new TronWeb({ fullHost: 'https://api.trongrid.io' })
      setTronWeb(tronWebInstance)
    } catch (error) {
      console.error("Failed to initialize TronWeb:", error);
      // Set tronWeb to null to prevent further errors
      setTronWeb(null);
    }
  }

  const isValidAddressByNetwork = (
    address: string,
    network: number
  ): boolean => {
    if (!address) return false
    switch (network) {
      case 1:
      case 2:
        return ethers.isAddress(address)
      case 3:
        return tronWeb?.isAddress(address)
      default:
        return false
    }
  }

  useEffect(() => {
    if (stepNewBeneficiary === 2) {
      // Add a small delay to ensure TronWeb is loaded
      const timer = setTimeout(() => {
        handleTronWeb();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [stepNewBeneficiary])

  useEffect(() => {
    if (!addressValue || !network || !addressBlur) return
    form.validateFields(['address'])
  }, [network, addressValue, addressBlur])

  // useEffect(() => {
  //   const values = form.getFieldsValue(requiredFields as any)
  //   const allFilled = requiredFields.every((field) => !!values[field])
  //   setIsAllFieldsFilled(allFilled)
  // }, [currency, network, address, form])

  useEffect(() => {
    if (!valueFormWallet && !valueSkipForm) return

    const valuesToSet: Partial<WalletBeneficiary> = {}

    const currency = valueSkipForm?.currency ?? valueFormWallet?.currency
    const network = valueSkipForm?.network ?? valueFormWallet?.network
    const address = valueSkipForm?.address ?? valueFormWallet?.address

    if (currency !== undefined) valuesToSet.currency = currency
    if (network !== undefined) valuesToSet.network = network
    if (address) valuesToSet.address = address

    form.setFieldsValue(valuesToSet)
  }, [valueFormWallet, valueSkipForm, stepNewBeneficiary])

  return (
    <Form
      style={{ display: stepNewBeneficiary === 2 ? 'block' : 'none' }}
      form={form}
      onFinish={(value) => {
        onSubmitWallet(value)
        setValueSkipForm(null)
        form.resetFields()
      }}
      onValuesChange={(_changeValue, _values) => {
        clearFieldErrorMessageOnChange(form)
        setValueSkipForm(_values)
      }}>
      <div className='form-content'>
        <Form.Item
          label='Crypto Currency'
          name='currency'
          validateTrigger='onBlur'
          rules={[{ required: true, message: 'This field is required' }]}>
          <Select
            placeholder='Select the crypto currency'
            suffixIcon={<ArrowIcon />}
            onChange={handleChangeCurrencyToken}
            optionLabelProp='label'>
            {metadataCurrencyToken.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}>
                  <span
                    className='ant-select-custom-value'
                    style={{ whiteSpace: 'nowrap' }}>
                    {item.label}
                  </span>
                  {searchCurrencyToken === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='Network'
          name='network'
          validateTrigger='onBlur'
          rules={[{ required: true, message: 'This field is required' }]}>
          <Select
            showSearch
            placeholder='Select the blockchain network'
            suffixIcon={<ArrowIcon />}
            onChange={handleChangeNetwork}
            optionLabelProp='label'>
            {metadataNetwork.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}>
                  <span
                    className='ant-select-custom-value'
                    style={{ whiteSpace: 'nowrap' }}>
                    {item.label}
                  </span>
                  {searchNetwork === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='Address'
          name='address'
          validateTrigger='onBlur'
          rules={[
            ...addressWalletRules,
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve()
                if (value && !network) return Promise.resolve()
                if (value && isValidAddressByNetwork(value, Number(network))) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error('Invalid address for selected network')
                )
              },
            },
          ]}>
          <Input
            onChange={(e) => setAddressValue(e?.target.value)}
            onBlur={() => setAddressBlur(true)}
            onFocus={() => setAddressBlur(false)}
            placeholder='Enter the wallet address'
          />
        </Form.Item>
        <Form.Item
          label='Name'
          name='name'
          validateTrigger='onBlur'
          rules={[
            { required: true, message: 'This field is required', whitespace: true },
            {
              min: 3,
              message: 'Minimum 3 characters',
              validateTrigger: 'onChange',
            },
            {
              max: 256,
              message: 'Maximum 256 characters',
              validateTrigger: 'onChange',
            },
          ]}>
          <Input disabled placeholder='e.g. ETH-ERC20-2f74' />
        </Form.Item>
      </div>
      <div className='form-footer'>
        <Typography.Text
          style={{
            color: '#F28A28',
            fontSize: isTablet ? 10 : 12,
            fontWeight: 400,
            lineHeight: '16px',
          }}>
          {
            "You'll be able to add more wallet addresses once the beneficiary is set up."
          }
        </Typography.Text>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}>
          <ButtonOutlinedCustom
            text={'Back'}
            size={isTablet ? 'medium' : 'large'}
            width={136}
            onClick={() => {
              onBack()
              form.resetFields()
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ButtonOutlinedCustom
              text={'Skip'}
              size={isTablet ? 'medium' : 'large'}
              width={126}
              onClick={() => {
                onSkip()
                form.resetFields()
              }}
            />
            <CustomButton
              type='submit'
              text={'Continue'}
              size={isTablet ? 'medium' : 'large'}
              disabled={!isAllFieldsFilled}
            />
          </div>
        </div>
      </div>
    </Form>
  )
}

export default FormWallet
